import { MergeOrUpdateStateAction } from '@tableau/vizportalclient-common-components';
import * as React from 'react';
import { ServiceLocator } from '../../app/ServiceLocator';
import { COLLECTION_NAME_MAX_LENGTH } from '../../app/spaces/Collections/CollectionHeaderEditableName';
import { UpdateCollectionsResponseData } from '../../app/spaces/Collections/CollectionModels';
import { CollectionsResourceQuery } from '../../app/spaces/Collections/ResourceQuery/CollectionsResourceQuery';
import { Messages } from '../../codegen/Localize';
import { ActionDialogBodyWithTextInput, ActionDialogBodyWithTextInputProps, ActionDialogBodyWithTextInputState } from '../../commonInfra/dialogs/ActionDialogBodyWithTextInput';
import { openReactActionDialog } from '../../commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';
import { toQPromise } from '../../commonInfra/utils/QUtils';
import { DataSources } from '../../content/resources/Datasources';
import { Workbooks } from '../../content/resources/Workbooks';
import { Flows } from '../../flow/resources/Flows';
import { IError } from '../../httpClient/serverApi/types/IError';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { Metrics } from '../../metrics/resources/Metrics';
import { Projects } from '../../projects/resources/Projects';
import { PublishedConnections } from '../../publishedConnection/resources/PublishedConnections';
import { Groups } from '../../userManagement/groups/Groups';
import { Views } from '../../views/resources/Views';
import { CollectionsErrorCode, CommonErrorCode, DatasourceErrorCode, FlowErrorCode, MetricErrorCode, ProjectErrorCode, SchedulingErrorCode, UserErrorCode, ViewErrorCode, WorkbookErrorCode } from '../helpers/ErrorCodes';
import { L10n } from '../L10n/L10n';
import { ExplorableItemType } from '../models/ContentType';
import { IContentInfo } from '../models/IContentInfo';
import { INamedItemInfo } from '../models/resourceInfoParts';
import { SchedulesService } from '../resources/Schedules';
import { ContentDraftStatus } from '../ServerApi/types/ContentDraft';
import { ToasterService, ToastType } from './ToasterService';

interface RenameActionParams {
  item: INamedItemInfo;
  dialogTitle: string;
  dialogDescription: string;
  renameFn: (id: string, name: string) => ng.IPromise<IResult>;
  getSuccessMessage: (newName: string) => string;
  defaultErrorMessage: string;
  maxNameLength?: number;
  errorMessageOnOpen?: string;
  isOwner?: boolean;
}

class RenameActionClass {

  public renamePublishedConnection(publishedConnection: INamedItemInfo): ng.IPromise<IResult> {
    return rename({
      item: publishedConnection,
      dialogTitle: Messages.renamePublishedConnection(),
      dialogDescription: Messages.confirmRenamePublishedConnection({ PUBLISHED_CONNECTION_NAME: L10n.elideIfLong(publishedConnection.name) }),
      renameFn: (id: string, name: string) => PublishedConnections.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.publishedConnectionRenamed({ PUBLISHED_CONNECTION_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.publishedConnectionRenameError({ PUBLISHED_CONNECTION_NAME: L10n.elideIfLong(publishedConnection.name) })
    });
  }

  public renameProject(project: INamedItemInfo): ng.IPromise<IResult> {
    return rename({
      item: project,
      dialogTitle: Messages.renameProject(),
      dialogDescription: Messages.confirmRenameProject({ PROJECT_NAME: L10n.elideIfLong(project.name) }),
      renameFn: (id: string, name: string) => Projects.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.projectRenamed({ PROJECT_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.projectRenameError({ PROJECT_NAME: L10n.elideIfLong(project.name) })
    });
  }

  public renameWorkbook(workbook: INamedItemInfo): ng.IPromise<IResult> {
    return rename({
      item: workbook,
      dialogTitle: Messages.renameWorkbook(),
      dialogDescription: Messages.confirmRenameWorkbook(),
      renameFn: (id: string, name: string) => Workbooks.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.workbookRenamed({ WORKBOOK_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.workbookRenameError({ WORKBOOK_NAME: L10n.elideIfLong(workbook.name) })
    });
  }

  public renameView(view: INamedItemInfo): ng.IPromise<IResult> {
    return rename({
      item: view,
      dialogTitle: Messages.renameView(),
      dialogDescription: Messages.confirmRenameView(),
      renameFn: (id: string, name: string) => Views.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.viewRenamed({ VIEW_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.viewRenameError({ VIEW_NAME: L10n.elideIfLong(view.name) })
    });
  }

  public renameSchedule(schedule: INamedItemInfo): ng.IPromise<IResult> {
    return rename({
      item: schedule,
      dialogTitle: Messages.renameSchedule(),
      dialogDescription: Messages.confirmRenameSchedule({ SCHEDULE_NAME: L10n.elideIfLong(schedule.name) }),
      renameFn: (id: string, name: string) => SchedulesService.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.updateScheduleNameSuccess({ ITEM_NAME: schedule.name, NEW_ITEM_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.scheduleRenameError({ SCHEDULE_NAME: L10n.elideIfLong(schedule.name) })
    });
  }

  public renameFlow(flow: IContentInfo<'flow'>): ng.IPromise<IResult> {
    const isNeverPublishedFlow = flow.draftStatus === ContentDraftStatus.unpublished;
    return rename({
      item: flow,
      dialogTitle: Messages.renameFlow(),
      dialogDescription: Messages.confirmRenameFlow({ FLOW_NAME: L10n.elideIfLong(flow.name) }),
      renameFn: (id: string, name: string) => isNeverPublishedFlow ? Flows.renameDraft(id, name) : Flows.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.flowRenamed({ FLOW_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.flowRenameError({ FLOW_NAME: L10n.elideIfLong(flow.name) })
    });
  }

  public renameMetric(metric: INamedItemInfo): ng.IPromise<IResult> {
    return rename({
      item: metric,
      dialogTitle: Messages.renameMetric(),
      dialogDescription: Messages.confirmRenameMetric({ METRIC_NAME: L10n.elideIfLong(metric.name) }),
      renameFn: (id: string, name: string) => Metrics.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.metricRenamed({ METRIC_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.metricRenameError({ METRIC_NAME: L10n.elideIfLong(metric.name) })
    });
  }

  public renameCollection(collection: IContentInfo<ExplorableItemType>, isOwner: boolean): ng.IPromise<IResult> {
    return rename({
      item: collection,
      dialogTitle: Messages.renameCollection(),
      dialogDescription: Messages.confirmRenameCollection({ COLLECTION_NAME: L10n.elideIfLong(collection.name) }),
      renameFn: (id: string, name: string) => {
        return (new CollectionsResourceQuery).update([{luid: collection.luid, name: name}])
        .then((response: UpdateCollectionsResponseData) => {
          if (response.errors && response.errors.length > 0) {
            return {errors: [{code: response.errors[0].errorCode, id: collection.id}]} as IResult;
          } else {
            return {};
          }
        })
        .catch((error: any) => {
          return {errors: [{code: error.data.errorCode, id: collection.id}]} as IResult;
        });
      },
      getSuccessMessage: (newName: string) => Messages.collectionRenamed({ COLLECTION_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.collectionRenameError({ COLLECTION_NAME: L10n.elideIfLong(collection.name) }),
      maxNameLength: COLLECTION_NAME_MAX_LENGTH,
      errorMessageOnOpen: collection.name.length > COLLECTION_NAME_MAX_LENGTH && Messages.createCollectionNameTooLongError(),
      isOwner,
    });
  }

  public renameGroup(group: INamedItemInfo): ng.IPromise<IResult> {
    return rename({
      item: group,
      dialogTitle: Messages.renameGroup(),
      dialogDescription: Messages.updateGroupNameDesc({ GROUP_NAME: L10n.elideIfLong(group.name) }),
      renameFn: (id: string, name: string) => toQPromise(Groups.updateGroupName(id, name)),
      getSuccessMessage: (newName: string) => Messages.updateGroupNameSuccessMsg({ ITEM_NAME: group.name, NEW_ITEM_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.groupRenameError({ GROUP_NAME: L10n.elideIfLong(group.name) })
    });
  }
  public renameDatasource(datasource: INamedItemInfo): ng.IPromise<IResult> {
    return rename({
      item: datasource,
      dialogTitle: Messages.renameDatasource(),
      dialogDescription: Messages.confirmRenameDatasource(),
      renameFn: (id: string, name: string) => DataSources.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.datasourceRenamed({ DATASOURCE_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.datasourceRenameError({ DATASOURCE_NAME: L10n.elideIfLong(datasource.name) })
    });
  }
}

function rename(params: RenameActionParams): ng.IPromise<IResult> {
  const {
    item, dialogTitle, dialogDescription, renameFn, getSuccessMessage, defaultErrorMessage, maxNameLength, errorMessageOnOpen, isOwner
  } = params;

  return openReactActionDialog<ActionDialogBodyWithTextInputProps, ActionDialogBodyWithTextInputState, IResult>({
    title: dialogTitle,
    bodyComponent: ActionDialogBodyWithTextInput,
    initialDialogState: {
      text: item.name,
      errorMessage: errorMessageOnOpen,
    },
    initialBodyProps: {
      originalText: item.name,
      label: dialogDescription,
      maxLength: maxNameLength || 255,
    },
    actionName: Messages.rename(),
    actionDisabled: (props, state) => state.text.length === 0 || state.text.length > props.maxLength || state.text === props.originalText,
    action: (_, state, updateDialogState) => executeRename({
      item,
      renameFn,
      defaultErrorMessage,
      getSuccessMessage,
      isOwner,
      newName: state.text,
      updateDialogState
    }),
    dialogWidth: TabDialogWidth.width375px,
    dismissDialogOnActionFailed: false,
  }).result;
}

type ExecuteRenameParams =
  Pick<RenameActionParams, 'item' | 'renameFn' | 'defaultErrorMessage' | 'getSuccessMessage' | 'isOwner'> &
  {
    updateDialogState: React.Dispatch<MergeOrUpdateStateAction<ActionDialogBodyWithTextInputState>>;
    newName: string;
  };

async function executeRename(
  { item, renameFn, defaultErrorMessage, getSuccessMessage, isOwner, newName, updateDialogState }: ExecuteRenameParams
) {
  const result: IResult = await renameFn(item.id, newName);
  if (result.errors && result.errors.length > 0) {
    const errorMessage = getErrorMessage(result.errors[0], defaultErrorMessage, item.name, newName, isOwner);
    updateDialogState({ errorMessage });
    return Promise.reject();
  } else {
    const successMessage = getSuccessMessage(newName);
    ToasterService.instance.pop({ type: ToastType.Success, text: successMessage });
  }
}

function getErrorMessage(error: IError, defaultErrorMessage: string, oldName?: string, newName?: string, isOwner?: boolean) {
  switch (error.code) {
    case FlowErrorCode.FlowNameAlreadyExists:
      return Messages.errorFlowNameAlreadyExists();
    case MetricErrorCode.MetricNameAlreadyExists:
      return Messages.errorMetricNameAlreadyExists();
    case ProjectErrorCode.ProjectNameAlreadyExists:
      return Messages.errorProjectNameAlreadyExists();
    case SchedulingErrorCode.SchedulingNameAlreadyExists:
      return Messages.errorScheduleNameAlreadyExists();
    case ViewErrorCode.ViewNameAlreadyExists:
      return Messages.errorViewNameAlreadyExists();
    case WorkbookErrorCode.WorkbookNameAlreadyExists:
      return Messages.errorWorkbookNameAlreadyExists();
    case CollectionsErrorCode.DUPLICATE_KEY:
      return isOwner ? Messages.collectionOwnerDuplicateRenameError(): Messages.collectionNonOwnerDuplicateRenameError();
    case UserErrorCode.UserGroupInvalidName:
        return Messages.errorUserGroupInvalidName();
    case UserErrorCode.UserGroupWithNameAlreadyExists:
        return Messages.errorUserGroupWithNameAlreadyExists();
    case UserErrorCode.UserAllUsersGroupAlreadyExists:
        return Messages.errorUserAllUsersGroupAlreadyExists({ NEW_ITEM_NAME: L10n.elideIfLong(newName) });
    case CommonErrorCode.GroupNotFound:
        return Messages.errorGroupNotFound({ ITEM_NAME: L10n.elideIfLong(oldName) });
    case DatasourceErrorCode.DatasourceWithSameNameInDestinationProject:
        return Messages.errorDatasourceWithSameNameInDestinationProject();
    default:
      return defaultErrorMessage;
  }
}


export const RenameAction = new RenameActionClass();
